import { FC, useCallback, useMemo, useState } from "react";
import { addHours, format } from "date-fns";
import { Control, useController } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Checkbox, Select } from "theme";

import {
  CircularProgress,
  Grid2, MenuItem,
  TableBody,
  TableCell,
  TableRow, useTheme,
} from "@mui/material";
import { styled } from "@mui/system";

import useEquipment from "../../../../api/hooks/useEquipment/useEquipment.ts";
import {
  StyledTable,
  StyledTableCell,
  StyledTableContainer,
  StyledTableHead,
} from "../../../../theme/components/Table/Table.tsx";
import { useBooking } from "../../../../utils/context/Booking/BookingContext.tsx";

type EquipmentGroupProps = {
  name: string;
  control: Control<any>;
}

const StyledImage = styled("img")(({ theme }) => {
  return {
    height: "40px",
    width: "40px",
    objectFit: "contain",
    backgroundColor: theme.palette.common.white,
  };
});

const EquipmentGroup: FC<EquipmentGroupProps> = ({ control, name }) => {
  const { field } = useController({
    control,
    name,
  });
  const { id } = useParams();
  const { booking } = useBooking();
  const { palette } = useTheme();

  const startDate = new Date((booking?.slotDate.includes("Z") ? booking?.slotDate.slice(0, -1) : booking?.slotDate) || Date.now());
  const endDate = addHours(startDate, booking?.hoursNumber || 0);
  const { equipment, isFetching } = useEquipment({
    startDate: format(new Date(startDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
    endDate: format(new Date(endDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
    bookingId: id !== 'new' ? id : null
  });

  const options = useMemo(() => {
    const mappedResult = [];

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [_, equipments] of Object.entries(equipment)) {
      mappedResult.push(...equipments);
    }

    return mappedResult;
  }, [equipment]);

  const [value, setValue] = useState<any[]>(field.value || []);

  const onMultipleChange = useCallback(
    (newValue: any[]) => {
      field.onChange(newValue);

      setValue(newValue);
    },
    [field],
  );

  const handleChange = useCallback(
    (checked: boolean, price: number, id: number, imageUrl: string, title: string, isFixedPrice: boolean) => {
      const changedEl = {
        id,
        imageUrl,
        title,
        count: 1,
        range: booking?.hoursNumber || 1,
        pricePerUnit: price,
        isFixedPrice
      };

      onMultipleChange(checked ? [...value, changedEl] : value.filter((i) => i.id !== id));
    },
    [booking?.hoursNumber, onMultipleChange, value],
  );

  const handleOptionsChange = useCallback(
    (id: number, count: number, range: number) => {
      onMultipleChange(
        ...[
          value.map((el) => {
            if (el.id === id) {
              return {
                ...el,
                count,
                range,
              };
            }

            return el;
          }),
        ],
      );
    },
    [onMultipleChange, value],
  );

  if (isFetching) {
    return (
      <Grid2 mt={2} container justifyContent="center">
        <CircularProgress color="secondary" />
      </Grid2>
    );
  }

  return (
    <StyledTableContainer sx={{ maxWidth: 690, maxHeight: 470 }}>
      <StyledTable aria-label="simple table">
        <StyledTableHead>
          <TableRow>
            <TableCell>Фото</TableCell>
            <TableCell>Назва</TableCell>
            <TableCell>Кількість</TableCell>
            <TableCell>Ціна за годину</TableCell>
            <TableCell>Кількість годин</TableCell>
            <TableCell>Обрати</TableCell>
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {options.map((row) => {
            const selectedRow = value.find((el) => el.id === row.id);

            const checked = !!selectedRow;
            const disabled = row.availableCount === 0;

            return <TableRow
              key={row.id}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <StyledTableCell>
                <StyledImage src={row.imageUrl} />
              </StyledTableCell>
              <StyledTableCell>{row.title}</StyledTableCell>
              <StyledTableCell>
                <Select
                  disabled={!checked || disabled}
                  fullWidth
                  sx={{
                    "& .MuiSelect-select": {
                      paddingTop: "2px",
                      paddingBottom: "2px",
                    },
                  }}
                  MenuProps={{
                    sx: {
                      "& .MuiPaper-root": {
                        background: palette.primary.main,
                      },
                    },
                  }}
                  value={selectedRow?.count || 1}
                  onChange={(e) => {
                    handleOptionsChange(row.id, Number(e.target.value), selectedRow?.range);
                  }}
                >
                  {Array.from({ length: row.availableCount }, (_, i) => i + 1).map((el) => {
                    return (
                      <MenuItem key={el} value={el}>
                        {el}
                      </MenuItem>
                    );
                  })}
                </Select>
              </StyledTableCell>
              <StyledTableCell>{row.pricePerUnit}₴</StyledTableCell>
              <StyledTableCell>
                <Select
                  disabled={!checked || disabled}
                  fullWidth
                  sx={{
                    "& .MuiSelect-select": {
                      paddingTop: "2px",
                      paddingBottom: "2px",
                    },
                  }}
                  MenuProps={{
                    sx: {
                      "& .MuiPaper-root": {
                        background: palette.primary.main,
                      },
                    },
                  }}
                  value={selectedRow?.range || booking?.hoursNumber}
                  onChange={(e) => {
                    handleOptionsChange(row.id, selectedRow?.count, Number(e.target.value));
                  }}
                >
                  {Array.from({ length:  booking?.hoursNumber ? booking?.hoursNumber/0.5 : 2 }, (_, i) => i + 1).map((el) => {
                    return (
                      <MenuItem key={el} value={el*0.5}>
                        {el*0.5} год
                      </MenuItem>
                    );
                  })}
                </Select>
              </StyledTableCell>
              <StyledTableCell>
                <Checkbox checked={checked} disabled={disabled}
                          onChange={(_, checked) => handleChange(checked, row.pricePerUnit, row.id, row.imageUrl, row.title, row.isFixedPrice)} />
              </StyledTableCell>
            </TableRow>;
          })}
        </TableBody>
      </StyledTable>
    </StyledTableContainer>
  );
};

export default EquipmentGroup;
